import { Link, type LinkProps } from 'expo-router';
import { type ReactNode } from 'react';
import { StyleSheet, View } from 'react-native';

import { IconCatering, IconDelivery, IconStore } from '@fhs/ui';
import { AlertDialog, IconChevronRight, Text, XStack, YStack, tokens } from '@fhs/ui';
import { useClickLogs } from '@fhs-legacy/frontend/src/hooks/use-click-logs';

import { useHrefs } from '../use-hrefs';

interface ServiceModeOptionProps extends LinkProps {
  icon: ReactNode;
  title: string;
  description: string;
}

function ServiceModeOption({
  icon,
  title,
  description,
  target,
  rel,
  ...linkProps
}: ServiceModeOptionProps) {
  const newTab = target === '_blank';

  return (
    <Link
      {...linkProps}
      target={target}
      rel={newTab ? (rel ?? 'noopener noreferrer') : rel}
      accessibilityLabel={newTab ? `${title}, ${description}. Opens in a new tab.` : undefined}
      style={[styles.optionCard, linkProps.style]}
    >
      <XStack style={styles.optionContent}>
        <XStack style={styles.iconContainer}>
          <View accessible={false} accessibilityElementsHidden>
            {icon}
          </View>
        </XStack>
        <YStack style={styles.textContainer}>
          <Text.Heading type="four">{title}</Text.Heading>
          <Text.Paragraph size="sm" style={styles.optionDescription}>
            {description}
          </Text.Paragraph>
        </YStack>
        <IconChevronRight
          accessible={false}
          aria-hidden
          size={24}
          color={tokens.colors.$black}
        />
      </XStack>
    </Link>
  );
}

type ServiceModeSelectDialogProps = {
  isOpen: boolean;
  onClose: () => void;
};

export function ServiceModeSelectDialog({ isOpen, onClose }: ServiceModeSelectDialogProps) {
  const hrefs = useHrefs();
  const { logPickUpOrderClickEvent, logDeliveryOrderClickEvent, logCateringOrderClickEvent } =
    useClickLogs();

  return (
    <AlertDialog
      visible={isOpen}
      onClose={onClose}
      onRequestClose={onClose}
      accessibilityLabel="Choose an Order Type"
      headingContent="Choose an Order Type"
      style={styles.dialog}
      bodyContent={
        <YStack style={styles.optionsContainer}>
          <ServiceModeOption
            title="Pick Up"
            description="Choose take out or dine in"
            icon={<IconStore size={24} />}
            href={hrefs.pickUpHref}
            onPress={() => {
              logPickUpOrderClickEvent();
              onClose();
            }}
          />
          {hrefs.deliveryHref && (
            <ServiceModeOption
              title="Delivery"
              description="Special Fees Apply"
              icon={<IconDelivery size={24} />}
              href={hrefs.deliveryHref}
              onPress={() => {
                logDeliveryOrderClickEvent();
                onClose();
              }}
            />
          )}
          {hrefs.cateringHref && (
            <ServiceModeOption
              title="Catering"
              description="Feeding a crowd?"
              icon={<IconCatering size={24} />}
              target={/^https?:/.test(hrefs.cateringHref) ? '_blank' : '_self'}
              href={hrefs.cateringHref}
              onPress={() => {
                logCateringOrderClickEvent();
                onClose();
              }}
            />
          )}
        </YStack>
      }
    />
  );
}

const styles = StyleSheet.create({
  dialog: {
    width: '100%',
    gap: 28,
  },
  optionsContainer: {
    gap: 14,
  },
  optionCard: {
    backgroundColor: tokens.colors.$white,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: tokens.colors.$blackOpacity10,
    padding: 24,
    minHeight: 90,
    flexGrow: 1,
  },
  optionContent: {
    alignItems: 'center',
    width: '100%',
    gap: 12,
  },
  iconContainer: {
    backgroundColor: tokens.colors.$blackOpacity04,
    borderRadius: 50,
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 40,
    minHeight: 40,
  },
  textContainer: {
    flexGrow: 1,
    flexShrink: 1,
  },
  optionDescription: {
    color: tokens.colors.$blackOpacity75,
  },
});
